import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import './index.css';

import Login from './pages/Login';
import Layout from './components/Layout';
import Dashboard from './pages/Dashboard';
import MyHolidays from './pages/MyHolidays';
import RequestHoliday from './pages/RequestHoliday';
import DirectorDashboard from './pages/DirectorDashboard';
import ManageRequests from './pages/ManageRequests';
import TeamCalendar from './pages/TeamCalendar';
import AdminUsers from './pages/AdminUsers';
import ChangePassword from './pages/ChangePassword';
import Onboarding from './pages/Onboarding';
import CompanyDocuments from './pages/CompanyDocuments';
import MyDocuments from './pages/MyDocuments';
import Recruitment from './pages/Recruitment';
import Recruits from './pages/Recruits';
import StockTake, { NoStockTakeAccess } from './pages/StockTake';
import CrmCustomers, { NoCrmAccess } from './pages/CrmCustomers';
import CrmCustomer from './pages/CrmCustomer';
import CrmFollowUps from './pages/CrmFollowUps';
import CrmInsights from './pages/CrmInsights';
import SalesBoard from './pages/SalesBoard';
import SalesBoardTV from './pages/SalesBoardTV';

const PrivateRoute = ({ children, roles }) => {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
};

const AppRoutes = () => {
  const { user } = useAuth();
  const isRecruit = user?.role === 'recruit';
  // Recruits are limited to the onboarding area until converted to staff.
  const staffOnly = (el) => (isRecruit ? <Navigate to="/onboarding" replace /> : el);
  // Stock Take is switched on per person by an admin (admins always have it).
  // The server checks again on every request; this just decides what to show.
  const stockTakeOnly = (el) => (user?.can_stock_take ? el : <NoStockTakeAccess />);
  // Sales Board viewing is the same idea: admins, plus anyone switched on in Manage Staff.
  // The CRM works the same way: switched on per person, checked again by the server.
  const crmOnly = (el) => (user?.can_use_crm ? el : <NoCrmAccess />);
  // Customer insights has its own switch (margins are sensitive), checked again by the server.
  const insightsOnly = (el) => (user?.can_insights ? el : <Navigate to="/" replace />);
  const salesBoardOnly = (el) => (user?.can_sales_board ? el : <Navigate to="/" replace />);

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/" replace /> : <Login />} />
      {/* Office screen: full page, no sidebar. Sales Board access only (the server checks too). */}
      <Route path="/sales-board/tv" element={<PrivateRoute>{salesBoardOnly(<SalesBoardTV />)}</PrivateRoute>} />
      <Route path="/" element={<PrivateRoute><Layout /></PrivateRoute>}>
        <Route index element={isRecruit ? <Navigate to="/onboarding" replace /> : <Dashboard />} />
        <Route path="onboarding" element={<Onboarding />} />
        <Route path="company-documents" element={<CompanyDocuments />} />
        <Route path="my-holidays" element={staffOnly(<MyHolidays />)} />
        <Route path="request" element={staffOnly(<RequestHoliday />)} />
        <Route path="team-calendar" element={staffOnly(<TeamCalendar />)} />
        <Route path="my-documents" element={staffOnly(<MyDocuments />)} />
        <Route path="stock-take" element={staffOnly(stockTakeOnly(<StockTake />))} />
        <Route path="crm" element={staffOnly(crmOnly(<CrmCustomers />))} />
        <Route path="crm/customers/:id" element={staffOnly(crmOnly(<CrmCustomer />))} />
        <Route path="crm/follow-ups" element={staffOnly(crmOnly(<CrmFollowUps />))} />
        <Route path="crm/insights" element={staffOnly(insightsOnly(<CrmInsights />))} />
        <Route path="change-password" element={<ChangePassword />} />
        <Route path="director" element={<PrivateRoute roles={['director','admin']}><DirectorDashboard /></PrivateRoute>} />
        <Route path="manage-requests" element={<PrivateRoute roles={['director','admin']}><ManageRequests /></PrivateRoute>} />
        <Route path="recruitment" element={<PrivateRoute roles={['director','admin']}><Recruitment /></PrivateRoute>} />
        <Route path="recruits" element={<PrivateRoute roles={['director','admin']}><Recruits /></PrivateRoute>} />
        <Route path="admin/users" element={<PrivateRoute roles={['admin']}><AdminUsers /></PrivateRoute>} />
        <Route path="sales-board" element={staffOnly(salesBoardOnly(<SalesBoard />))} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

const App = () => (
  <BrowserRouter>
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
  </BrowserRouter>
);

export default App;
